<script setup lang="ts">
/**
 * RegisterStep3Pending — pending-approval state after successful registration.
 * Mirrors resident-register.html STEP 3 panel exactly.
 * Shows what-happens-next timeline; next steps 1+2 are done, 3+4 are pending.
 */
defineProps<{
  fullName?: string;
  address?: string;
  verificationMethod?: string;
}>();
</script>

<template>
  <div data-step="3" data-testid="register-step-3">
    <!-- Pending banner — amber tones (not teal — amber is warning/pending) -->
    <div class="pending-banner">
      <div class="pending-circle">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      </div>
      <h2>Account submitted — pending board review</h2>
      <p>
        Thanks{{ fullName ? ` ${fullName.split(' ')[0]}` : '' }}. Your account is created and verification is on its way.
        The HOA board typically approves new residents within
        <strong style="color: var(--fg);">1–2 business days</strong>.
      </p>
    </div>

    <!-- What happens next timeline -->
    <div class="pending-detail">
      <div class="pending-section-label">What happens next</div>

      <div class="next-step-row done">
        <div class="next-step-num"><span>1</span></div>
        <div class="grow">
          <div class="nsr-label">Account created</div>
          <div class="nsr-sub">{{ address || 'Your address' }}</div>
        </div>
        <div class="nsr-time font-mono tabular-nums">just now</div>
      </div>

      <div class="next-step-row done">
        <div class="next-step-num"><span>2</span></div>
        <div class="grow">
          <div class="nsr-label">
            <template v-if="verificationMethod === 'MailedCode'">Verification code mailed</template>
            <template v-else-if="verificationMethod === 'DocumentUpload'">Document upload received</template>
            <template v-else-if="verificationMethod === 'NeighborVouch'">Neighbor vouch requested</template>
            <template v-else>Verification initiated</template>
          </div>
          <div v-if="verificationMethod === 'MailedCode'" class="nsr-sub">
            USPS first-class to {{ address || 'your address' }} · expect 3–5 business days
          </div>
          <div v-else-if="verificationMethod === 'DocumentUpload'" class="nsr-sub">
            Board will review your documents privately
          </div>
          <div v-else class="nsr-sub">
            Vouch request sent to approved neighbors
          </div>
        </div>
        <div class="nsr-time font-mono tabular-nums">just now</div>
      </div>

      <div class="next-step-row">
        <div class="next-step-num"><span>3</span></div>
        <div class="grow">
          <div class="nsr-label">
            <template v-if="verificationMethod === 'MailedCode'">Enter your code when it arrives</template>
            <template v-else-if="verificationMethod === 'DocumentUpload'">Board reviews your document</template>
            <template v-else>Neighbor confirms your address</template>
          </div>
          <div v-if="verificationMethod === 'MailedCode'" class="nsr-sub">
            We'll email you a reminder. The code is 6 digits, printed on a single-page letter.
          </div>
          <div v-else class="nsr-sub">
            The board reviews privately. You'll hear back by email.
          </div>
        </div>
        <div class="nsr-time font-mono tabular-nums">~3–5 days</div>
      </div>

      <div class="next-step-row">
        <div class="next-step-num"><span>4</span></div>
        <div class="grow">
          <div class="nsr-label">Board reviews &amp; approves</div>
          <div class="nsr-sub">
            A board member double-checks your address against the HOA roster.
            You'll get an email when access is enabled. Sign in again then.
          </div>
        </div>
        <div class="nsr-time font-mono tabular-nums">~1 day after step 3</div>
      </div>
    </div>

    <!-- In the meantime actions -->
    <div class="reg-body" style="background: var(--bg-soft); border-top: 1px solid var(--border);">
      <div class="pending-section-label">In the meantime</div>
      <div class="cluster gap-10" style="margin-top: 6px;">
        <a href="/portal" class="btn btn-secondary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 9V5a3 3 0 0 0-6 0v4" />
            <rect x="3" y="11" width="18" height="10" rx="2" />
          </svg>
          Report an issue (anonymous)
        </a>
        <a href="/resident/login" class="btn btn-ghost">Try signing in</a>
      </div>
      <p style="font-size: 12.5px; color: var(--muted); margin: 18px 0 0; line-height: 1.55;">
        Questions about your application? Email
        <a href="mailto:board@hoawatch.us" style="color: var(--navy-700); font-weight: 600;">board@hoawatch.us</a>.
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Pending banner */
.pending-banner {
  background: linear-gradient(180deg, var(--amber-100), transparent);
  padding: 32px 32px 24px;
  text-align: center;
}
.pending-circle {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: var(--amber-100);
  color: var(--amber-700);
  display: grid;
  place-items: center;
  margin: 0 auto 18px;
  position: relative;
}
.pending-circle svg { width: 32px; height: 32px; }
.pending-circle::after {
  content: '';
  position: absolute;
  inset: -8px;
  border: 2px solid var(--amber-100);
  border-top-color: var(--amber-700);
  border-radius: 50%;
  animation: spin 2.5s linear infinite;
  opacity: 0.4;
}
@keyframes spin { to { transform: rotate(360deg); } }
.pending-banner h2 {
  font-family: var(--font-display);
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 6px;
}
.pending-banner p { color: var(--fg-soft); margin: 0 auto; max-width: 440px; font-size: 15px; line-height: 1.55; }

/* Next steps timeline */
.pending-detail {
  padding: 24px 32px;
  border-bottom: 1px solid var(--border);
}
@media (max-width: 640px) {
  .pending-detail { padding: 20px; }
}
.pending-section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted-2);
  margin-bottom: 12px;
}
.next-step-row {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 12px 0;
  border-top: 1px solid var(--border);
}
.next-step-row:first-of-type { border-top: 0; }
.next-step-num {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: var(--navy-100);
  color: var(--navy-700);
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
}
.next-step-row.done .next-step-num {
  background: var(--green-100);
  color: var(--green-700);
}
.next-step-row.done .next-step-num::after { content: '✓'; }
.next-step-row.done .next-step-num span { display: none; }
.nsr-label { font-weight: 600; font-size: 13.5px; }
.nsr-sub { font-size: 12.5px; color: var(--muted); margin-top: 2px; line-height: 1.5; }
.nsr-time { margin-left: auto; font-size: 11.5px; color: var(--muted); white-space: nowrap; }
.grow { flex: 1; }
</style>
